import { type RefObject, useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keyboard and screen-reader isolation shared by every modal surface. */
export function useModalFocus(ref: RefObject<HTMLElement>, onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const modalLayer = dialog.closest<HTMLElement>('.today-overlay-backdrop, .participation-sheet-backdrop') ?? dialog;
    const siblings = modalLayer.parentElement
      ? [...modalLayer.parentElement.children]
        .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== modalLayer)
        .map((element) => ({ element, ariaHidden: element.getAttribute('aria-hidden'), inert: element.hasAttribute('inert') }))
      : [];
    for (const { element } of siblings) {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    }
    dialog.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && (document.activeElement === dialog || document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === dialog || document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      for (const { element, ariaHidden, inert } of siblings) {
        if (ariaHidden == null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
        if (!inert) element.removeAttribute('inert');
      }
      if (previous?.isConnected) previous.focus();
    };
  }, [ref]);
}
